/**
 * Talking to the venue API. Every call says plainly what went wrong; a call made after the session
 * ended raises the sign-in sheet. The hall's manifest is also kept on the device, so a hall opened
 * once opens again without a connection.
 */

import type { EventRow, LayoutRow, LinkRow, LogRow, Me, PersonRow, Role, VersionRow } from '@mawkeb/shared/api';
import type { Layout } from '@mawkeb/shared/layout';
import { checkManifest, type FileAccess, type HallResponse } from '@mawkeb/shared/manifest';
import { session } from './state/session';

export class ApiError extends Error {
  constructor(
    /** The server's status, or 'offline' when it couldn't be reached. */
    readonly status: number | 'offline',
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('offline', 'There’s no connection right now.');
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* an answer that isn't JSON */
  }
  if (res.status === 401 && !path.startsWith('/api/auth/')) session().end();
  if (!res.ok) {
    const said = (data as { error?: unknown } | null)?.error;
    throw new ApiError(res.status, typeof said === 'string' ? said : `The venue server answered ${res.status}. Try again in a moment.`, data);
  }
  return data as T;
}

export const api = {
  me: () => request<{ me: Me | null }>('GET', '/api/me'),
  signIn: (email: string, password: string) => request<{ me: Me }>('POST', '/api/auth/signin', { email, password }),
  signOut: () => request<{ ok: true }>('POST', '/api/auth/signout', {}),
  changePassword: (current: string, next: string) => request<{ ok: true }>('POST', '/api/auth/password', { current, next }),
  setupCheck: (token: string) => request<{ email: string; name: string }>('POST', '/api/auth/setup/check', { token }),
  setup: (token: string, password: string) => request<{ me: Me }>('POST', '/api/auth/setup', { token, password }),

  people: () => request<{ people: PersonRow[] }>('GET', '/api/people'),
  addPerson: (email: string, name: string, role: Role) => request<{ person: PersonRow; link: string }>('POST', '/api/people', { email, name, role }),
  changePerson: (email: string, patch: { name?: string; role?: Role }) => request<{ person: PersonRow }>('PATCH', `/api/people/${encodeURIComponent(email)}`, patch),
  newSetupLink: (email: string) => request<{ link: string }>('POST', `/api/people/${encodeURIComponent(email)}/link`, {}),
  removePerson: (email: string) => request<{ ok: true }>('DELETE', `/api/people/${encodeURIComponent(email)}`),

  events: (deleted = false) => request<{ events: EventRow[] }>('GET', `/api/events${deleted ? '?deleted=1' : ''}`),
  makeEvent: (e: { name: string; startsOn?: string | null; endsOn?: string | null }) => request<{ event: EventRow }>('POST', '/api/events', e),
  changeEvent: (id: string, e: { name?: string; startsOn?: string | null; endsOn?: string | null }) => request<{ event: EventRow }>('PATCH', `/api/events/${id}`, e),
  deleteEvent: (id: string) => request<{ ok: true }>('DELETE', `/api/events/${id}`),
  restoreEvent: (id: string) => request<{ event: EventRow }>('POST', `/api/events/${id}/restore`, {}),
  event: (id: string) => request<{ event: EventRow; layouts: LayoutRow[]; deleted: LayoutRow[] }>('GET', `/api/events/${id}`),

  makeLayout: (eventId: string, l: { name: string; copyOf?: string; data?: Layout }) => request<{ layout: LayoutRow }>('POST', `/api/events/${eventId}/layouts`, l),
  layout: (id: string) => request<{ layout: LayoutRow; data: Layout; event: EventRow | null }>('GET', `/api/layouts/${id}`),
  saveLayout: (id: string, base: number, data: Layout) => request<{ version: number; savedAt: number; summary: string; unchanged?: boolean }>('PUT', `/api/layouts/${id}`, { base, data }),
  changeLayout: (id: string, patch: { name?: string; showReciters?: boolean }) => request<{ layout: LayoutRow }>('PATCH', `/api/layouts/${id}`, patch),
  deleteLayout: (id: string) => request<{ ok: true }>('DELETE', `/api/layouts/${id}`),
  restoreLayout: (id: string) => request<{ layout: LayoutRow }>('POST', `/api/layouts/${id}/restore`, {}),
  versions: (id: string) => request<{ versions: VersionRow[] }>('GET', `/api/layouts/${id}/versions`),
  version: (id: string, v: number) => request<{ version: number; data: Layout }>('GET', `/api/layouts/${id}/versions/${v}`),
  restoreVersion: (id: string, v: number, base: number) => request<{ version: number; savedAt: number; summary: string }>('POST', `/api/layouts/${id}/versions/${v}/restore`, { base }),
  log: (id: string) => request<{ log: LogRow[] }>('GET', `/api/layouts/${id}/log`),

  links: (id: string) => request<{ links: LinkRow[] }>('GET', `/api/layouts/${id}/links`),
  makeLink: (id: string) => request<{ link: LinkRow }>('POST', `/api/layouts/${id}/links`, {}),
  linkOff: (token: string) => request<{ ok: true }>('DELETE', `/api/links/${token}`),
};

export interface ViewResponse {
  layout: { name: string; version: number; savedAt: number; savedBy: string; data: Layout };
  event: { name: string };
  hall: HallResponse;
}

export function viewUrl(token: string): string {
  return `${location.origin}/v/${token}`;
}

const API_CACHE = 'venue-api-v1';

export class HallError extends Error {
  constructor(readonly kind: 'offline' | 'missing' | 'server' | 'damaged' | 'signed-out', message: string) {
    super(message);
  }
}

async function remember(key: string, body: unknown) {
  try {
    const cache = await caches.open(API_CACHE);
    await cache.put(`${location.origin}/__venue/${key}`, new Response(JSON.stringify(body), { headers: { 'content-type': 'application/json' } }));
  } catch {
    /* storage blocked: the hall still opens while online */
  }
}

async function recall<T>(key: string): Promise<T | null> {
  try {
    const cache = await caches.open(API_CACHE);
    const hit = await cache.match(`${location.origin}/__venue/${key}`);
    return hit ? ((await hit.json()) as T) : null;
  } catch {
    return null;
  }
}

export async function forgetHalls() {
  try {
    await caches.delete(API_CACHE);
  } catch {
    /* nothing kept */
  }
}

let early: { id: string; res: Promise<Response> } | null = null;

/** Starts asking for a hall before the interface has mounted, so the answer is on its way sooner. */
export function prefetchHall(id: string) {
  early = { id, res: fetch(`/api/halls/${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store' }) };
  early.res.catch(() => {});
}

function readHall(body: HallResponse): HallResponse {
  try {
    checkManifest(body.manifest);
  } catch (e) {
    throw new HallError('damaged', `The hall’s description couldn’t be read (${(e as Error).message}).`);
  }
  return body;
}

export async function fetchHall(id: string): Promise<HallResponse & { offline: boolean }> {
  let res: Response;
  try {
    const head = early?.id === id ? early.res : null;
    early = null;
    res = await (head ?? fetch(`/api/halls/${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store' }));
  } catch {
    const saved = await recall<HallResponse>(`hall/${id}`);
    if (saved) return { ...saved, offline: true };
    throw new HallError('offline', 'There is no connection, and this hall hasn’t been opened on this device before.');
  }
  if (res.status === 401) {
    session().end();
    throw new HallError('signed-out', 'Sign in to open the hall.');
  }
  if (res.status === 404) throw new HallError('missing', 'This hall hasn’t been published yet.');
  if (!res.ok) {
    const saved = await recall<HallResponse>(`hall/${id}`);
    if (saved) return { ...saved, offline: true };
    throw new HallError('server', `The venue server answered ${res.status}. Try again in a minute.`);
  }
  let body: HallResponse;
  try {
    body = readHall((await res.json()) as HallResponse);
  } catch (e) {
    throw e instanceof HallError ? e : new HallError('damaged', 'The hall’s description couldn’t be read.');
  }
  void remember(`hall/${id}`, body);
  return { ...body, offline: false };
}

/** The hall model only, for drawing layout previews: from the device if it has one, else the server. */
export async function hallModel(id: string): Promise<HallResponse['manifest']['model'] | null> {
  const saved = await recall<HallResponse>(`hall/${id}`);
  if (saved) return saved.manifest.model;
  try {
    return (await fetchHall(id)).manifest.model;
  } catch {
    return null;
  }
}

/** A fresh pass to the hall's files, when the old one has run out. */
export async function renewAccess(id: string): Promise<FileAccess> {
  const res = await fetch(`/api/halls/${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store' });
  if (res.status === 401) session().end();
  if (!res.ok) throw new HallError('server', `renewing the file pass failed (${res.status})`);
  const body = (await res.json()) as HallResponse;
  void remember(`hall/${id}`, body);
  return body.access;
}

/** The layout and hall behind a view-only link. */
export async function fetchView(token: string): Promise<ViewResponse & { offline: boolean }> {
  let res: Response;
  try {
    res = await fetch(`/api/view/${token}`, { credentials: 'omit', cache: 'no-store' });
  } catch {
    const saved = await recall<ViewResponse>(`view/${token}`);
    if (saved) return { ...saved, offline: true };
    throw new HallError('offline', 'There is no connection, and this link hasn’t been opened on this device before.');
  }
  if (res.status === 404) {
    const said = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
    throw new HallError('missing', said ?? 'This link was turned off, or the layout was deleted.');
  }
  if (!res.ok) throw new HallError('server', `The venue server answered ${res.status}. Try again in a minute.`);
  const body = (await res.json()) as ViewResponse;
  readHall(body.hall);
  void remember(`view/${token}`, body);
  return { ...body, offline: false };
}

export async function renewViewAccess(token: string): Promise<FileAccess> {
  return (await fetchView(token)).hall.access;
}
